import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import FormTwoColumnContainer from "../../shared-components/FormTwoColumnContainer";
import DropdownComboSimple, {
  DropdownItem as DropdownItemSimple,
} from "../../shared-components/DropdownComboSimple";
import DropdownCombo, {
  DropdownItem,
} from "../../shared-components/DropdownCombo";

import {
  useCompaniesFetchAll,
  useGetCompanyWithBlueprint,
} from "../../api/CompaniesApi";
import {
  useGetAllBlueprints,
  useGetBehaviorKits,
} from "../../api/BlueprintsApi";
import { classNames, dropdownItemSortEval } from "../../utils";
import { BehaviorKit, CompanyWithBlueprintInfo } from "../../api/Companies";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useFamaFlags } from "../../api/FamaFlags";
import CheckTypesMulti from "../form-multi/CheckTypesMulti";
import { BlueprintMinimalInfo } from "../../api/Blueprints";
import LabeledCheckbox from "../../shared-components/LabeledCheckbox";
import { usePermissionValidator } from "../../api/UserPermissionValidators";
import InputField from "../../shared-components/InputField";
import { REPORT_TYPES } from "../../api/Reports";

type Props = {
  onChange: (data: {
    companyUUID?: string;
    blueprintUUIDs: string[];
    behaviorKitUUID?: string;
    clientQa?: boolean;
    internalQa?: boolean;
    pulseChecked?: boolean;
    pulseVerified?: boolean;
    frequency?: number;
  }) => void;
  showErrors?: { checkbox: boolean; frequency: boolean };
};

function validateAndParseFrequency(value: string) {
  const MAX_FREQUENCY = 99;
  try {
    const parsed = parseInt(value);
    if (isNaN(parsed)) return 0;
    if (parsed > MAX_FREQUENCY) return MAX_FREQUENCY;
    return parsed;
  } catch (error) {
    return 0;
  }
}

function isMisconductSelected(blueprintChecks: BlueprintMinimalInfo[]) {
  return (
    blueprintChecks.filter((c) => c.report_type === REPORT_TYPES.MISCONDUCT)
      .length > 0
  );
}

function isThreeSixtySelected(blueprintChecks: BlueprintMinimalInfo[]) {
  return (
    blueprintChecks.filter((c) => c.report_type === REPORT_TYPES.THREE_SIXTY)
      .length > 0
  );
}

export const ReportBulkSelections = ({ onChange, showErrors }: Props) => {
  const [companyItems, setCompanyItems] = useState<DropdownItem[]>([]);
  const [blueprintItems, setBlueprintItems] = useState<BlueprintMinimalInfo[]>(
    []
  );
  const [behaviorKitItems, setBehaviorKitItems] = useState<
    DropdownItemSimple[]
  >([]);
  const [companyUUID, setCompanyUUID] = useState<string>();
  const [behaviorKitUUID, setBehaviorKitUUID] = useState<string>();
  const [clientQa, setClientQa] = useState<boolean>();
  const [internalQa, setInternalQa] = useState<boolean>();
  const [pulseChecked, setPulseChecked] = useState<boolean>(false);
  const [pulseVerified, setPulseVerified] = useState<boolean>(false);
  const [frequency, setFrequency] = useState<number>(0);
  const { showBehaviorKit } = useFamaFlags();
  const { canInternalQaView, person } = usePermissionValidator();
  const verifyRecurText =
    "By checking this box, you are signing up for an Employee Screening Subscription for this individual and agree to be billed for each screening at your contracted rates. Contact your Fama Account Manager for subscription prices.";

  const {
    mutate: getAllCompanies,
    data: companiesInfo,
    isLoading: isLoadingCompanies,
  } = useCompaniesFetchAll();
  const {
    mutate: getBlueprints,
    data: blueprintsList,
    isLoading: isLoadingBlueprints,
  } = useGetAllBlueprints();
  const {
    mutate: getCompaniesInfoWithBlueprint,
    data: companiesWithBlueprint,
    isLoading: isLoadingCompanyBlueprints,
  } = useGetCompanyWithBlueprint();
  const { mutate: getBehaviorKits, data: behaviorKitList } =
    useGetBehaviorKits();
  const initialBluePrintCheck = useRef<unknown>(null);
  const initialBehaviorKitChange = useRef<unknown>(null);

  const handleBehaviorKitChange = (item: DropdownItemSimple) => {
    setBehaviorKitUUID(item.id);
    initialBehaviorKitChange.current = false;
  };
  const [blueprintChecks, setBlueprintChecks] = useState<
    BlueprintMinimalInfo[]
  >([]);

  useEffect(() => {
    getAllCompanies();
    getBlueprints();
  }, [getAllCompanies, getBlueprints]);

  const handleCompanyChange = useCallback(
    (item: DropdownItem) => {
      if (!blueprintsList) return;
      getCompaniesInfoWithBlueprint({
        company_uuids: [item.id],
        blueprintsList: blueprintsList,
      });
      setCompanyUUID(item.id);
      setBehaviorKitUUID("");
    },
    [blueprintsList, getCompaniesInfoWithBlueprint]
  );

  const createBehaviorsKitItems = (
    company: CompanyWithBlueprintInfo | null,
    behaviorKList: BehaviorKit[] | undefined
  ) => {
    if (behaviorKList && behaviorKList.length > 0) {
      const list = behaviorKList;
      const items: DropdownItem[] = list.map((element) => {
        return {
          id: element.external_id ?? "",
          label: element.name,
        };
      });
      items.push({ id: "NULL", label: "General" });
      items.sort(dropdownItemSortEval);

      let default_bk = "null";
      if (company?.default_behavior_kit) {
        default_bk = company?.default_behavior_kit.toLowerCase();
      }
      const sort_by_default_bk = (a: DropdownItem, b: DropdownItem) => {
        if (a.id.toLowerCase() === default_bk) {
          return -1;
        }
        return 0;
      };
      items.sort(sort_by_default_bk);
      return items;
    } else {
      const emptyList: DropdownItem[] = [{ id: "NULL", label: "General" }];
      return emptyList;
    }
  };

  useEffect(() => {
    if (companyUUID) {
      getBehaviorKits(companyUUID);
    }
  }, [getBehaviorKits, companyUUID]);

  const selectedCompany = useMemo(() => {
    return (
      companiesWithBlueprint &&
      companiesWithBlueprint.length > 0 &&
      companiesWithBlueprint[0]
    );
  }, [companiesWithBlueprint]);

  useEffect(() => {
    if (companiesInfo && companiesInfo.length > 0) {
      const list = companiesInfo;
      const items: DropdownItem[] = list.map((element) => {
        return { id: element.external_id, label: element.name };
      });
      setCompanyItems(items);
    }

    if (companiesWithBlueprint && companiesWithBlueprint.length > 0) {
      const list = companiesWithBlueprint;
      const items: BlueprintMinimalInfo[] = list?.[0]?.blueprintsInfo
        .filter(
          (blueprint) => blueprint.report_type !== REPORT_TYPES.INSTANTFIT
        )
        .map((element) => {
          return {
            id: element.blueprint_uuid,
            label: element.name,
            blueprint_uuid: element.blueprint_uuid,
            name: element.name,
            report_type: element.report_type,
          };
        });
      setBlueprintItems(items);
      setClientQa(companiesWithBlueprint[0].client_qa || false);
      setInternalQa(companiesWithBlueprint[0].internal_qa || false);
    }
    if (selectedCompany) {
      const items = createBehaviorsKitItems(selectedCompany, behaviorKitList);
      setBehaviorKitItems(items);
      setBehaviorKitUUID(items[0].id);
    }
    return () => {
      initialBluePrintCheck.current = null;
      initialBehaviorKitChange.current = null;
    };
  }, [
    companiesInfo,
    blueprintsList,
    companiesWithBlueprint,
    behaviorKitList,
    setClientQa,
    setInternalQa,
    selectedCompany,
  ]);

  useEffect(() => {
    const blueprintUUIDs: string[] = [];
    for (const bp of blueprintChecks) {
      blueprintUUIDs.push(bp.blueprint_uuid);
    }
    onChange({
      companyUUID,
      blueprintUUIDs,
      behaviorKitUUID,
      clientQa,
      internalQa,
      pulseChecked,
      pulseVerified,
      frequency,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyUUID,
    blueprintChecks,
    behaviorKitUUID,
    clientQa,
    internalQa,
    pulseChecked,
    pulseVerified,
    frequency,
  ]);

  const onSourceChange = (active: boolean, bp_uuid: string) => {
    // always remove requested item first to prevent duplicates
    let newSources = blueprintChecks.filter((source) => {
      return source.blueprint_uuid !== bp_uuid;
    });
    if (active) {
      const found = blueprintItems.find((blueprint) => {
        return blueprint.blueprint_uuid === bp_uuid;
      });
      if (found) {
        let sameTypes = [found.report_type];
        if (
          [REPORT_TYPES.MISCONDUCT, REPORT_TYPES.THREE_SIXTY].includes(
            found.report_type
          )
        ) {
          sameTypes = [REPORT_TYPES.MISCONDUCT, REPORT_TYPES.THREE_SIXTY];
        }
        // remove other checks of same type as found
        newSources = newSources.filter(
          (item) => !sameTypes.includes(item.report_type)
        );
        newSources.push(found);
      }
    }

    if (!isMisconductSelected(newSources)) {
      setPulseChecked(false);
      setPulseVerified(false);
      setFrequency(0);
    }
    setBlueprintChecks(newSources);
  };

  const anyMisconductSelected = isMisconductSelected(blueprintChecks);
  const anyThreeSixtySelected = isThreeSixtySelected(blueprintChecks);

  return (
    <div>
      <FormTwoColumnContainer title="Screening Company">
        <DropdownCombo
          items={companyItems}
          disabled={isLoadingCompanies}
          onChange={handleCompanyChange}
          needTypeAhead={true}
          label={"Select Company*"}
          placeHolder={isLoadingCompanies ? "Loading" : "Select Company"}
          containerClassName="w-full"
          buttonContainerClassName="w-full"
          name="selected-company"
        ></DropdownCombo>
      </FormTwoColumnContainer>
      {/* Blueprint Section */}
      <FormTwoColumnContainer title="Check Type">
        {(blueprintItems.length === 0 ||
          isLoadingBlueprints ||
          isLoadingCompanyBlueprints) && (
          <DropdownComboSimple
            items={[]}
            disabled={true}
            placeHolder={
              isLoadingBlueprints || isLoadingCompanyBlueprints
                ? "Loading"
                : "Please Select Company First"
            }
            label={"Select Check Type*"}
            name="selected-check-type"
          ></DropdownComboSimple>
        )}

        {blueprintItems.length !== 0 && (
          <div className="flex-1">
            <span className="block mb-2 text-sm font-medium text-gray-600">
              Check Type*
            </span>
            <div className="bg-white p-4 border border-gray-300 rounded-lg h-full flex-1">
              <CheckTypesMulti
                blueprintItems={blueprintItems}
                selections={blueprintChecks}
                onChange={onSourceChange}
              />
            </div>
          </div>
        )}
        {behaviorKitItems &&
          showBehaviorKit &&
          (anyMisconductSelected || anyThreeSixtySelected) && (
            <div className="flex items-center">
              <div className="w-96">
                <DropdownComboSimple
                  containerClassName="mt-5"
                  items={behaviorKitItems}
                  {...(initialBehaviorKitChange.current === null && {
                    initial: behaviorKitItems[0],
                  })}
                  onChange={handleBehaviorKitChange}
                  label={"Select Behavior Kit*"}
                  name="selected-behavior-kit"
                  icon={
                    <InformationCircleIcon
                      height={24}
                      width={24}
                      color="grey"
                    />
                  }
                  tooltip="Behavior kits are the configurable filters that companies can use to set behavior flags, lookback periods, and keywords for any given check type.
                The General Behavior Kit utilizes configured settings derived from the selected Check Type."
                ></DropdownComboSimple>
              </div>
            </div>
          )}
        {selectedCompany &&
          anyMisconductSelected &&
          (selectedCompany.client_qa || selectedCompany.internal_qa) && (
            <div className="flex-1">
              <span className="block mt-4 mb-2 text-sm font-medium text-gray-600">
                QA*
              </span>
              <div className="bg-white p-4 border border-gray-300 rounded-lg h-full flex-1">
                {selectedCompany.internal_qa && canInternalQaView && (
                  <LabeledCheckbox
                    key="internal_qa"
                    id="internal_qa"
                    label="Internal Investigator"
                    name="Internal Investigator"
                    preserveLabelCasing={true}
                    onChange={() => setInternalQa(!internalQa)}
                    disabled={false}
                    checked={internalQa}
                  ></LabeledCheckbox>
                )}
                {selectedCompany.client_qa && (
                  <LabeledCheckbox
                    key="client_qa"
                    id="client_qa"
                    label="Client Investigator"
                    name="Client Investigator"
                    onChange={() => setClientQa(!clientQa)}
                    disabled={false}
                    checked={clientQa}
                  ></LabeledCheckbox>
                )}
              </div>
            </div>
          )}
      </FormTwoColumnContainer>
      {anyMisconductSelected && person.canCreateRecur && (
        <FormTwoColumnContainer title="Employee Screening Subscription">
          <div className="flex">
            <div className="flex-1 mr-4">
              <span className="block mb-2 text-sm font-medium text-gray-600">
                Select the box to set up employee rescreening
              </span>
              <div className="bg-white p-4 border border-gray-300 rounded-lg">
                <LabeledCheckbox
                  name="pulse"
                  label="Add Automated Employee Screening"
                  checked={pulseChecked}
                  onChange={(e) => setPulseChecked(e.target.checked)}
                ></LabeledCheckbox>
              </div>
            </div>
            {pulseChecked && (
              <div className="flex-1">
                <InputField
                  value={frequency}
                  type={"number"}
                  name={"frequency"}
                  min={1}
                  max={99}
                  className={classNames(
                    "w-20 p-4",
                    showErrors?.frequency ? "border-red-500" : "border-our-blue"
                  )}
                  readOnly={!pulseChecked}
                  label={"Frequency"}
                  tooltip={"Enter the frequency of checks in months"}
                  tooltipClassName="inline-flex w-3.5 text-gray-500"
                  handleChange={(e) => {
                    setFrequency(validateAndParseFrequency(e.target.value));
                  }}
                  units="Months"
                />
              </div>
            )}
          </div>
          {pulseChecked && (
            <>
              <LabeledCheckbox
                name="verify-recur"
                label={verifyRecurText}
                defaultChecked={pulseVerified}
                alignCheckTop={true}
                preserveLabelCasing={true}
                classes={classNames(
                  "bg-white border-2 rounded-lg m-1 mt-5 p-2 ",
                  showErrors?.checkbox ? "border-red-500" : "border-our-blue"
                )}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setPulseVerified(e.target.checked);
                }}
              ></LabeledCheckbox>
              {showErrors?.checkbox && (
                <div className="text-red-500">
                  Please select the confirmation checkbox
                </div>
              )}
              {showErrors?.frequency && (
                <div className="text-red-500">
                  Please enter a frequency and make sure it is greater than 0
                </div>
              )}
            </>
          )}
        </FormTwoColumnContainer>
      )}
    </div>
  );
};

export default ReportBulkSelections;
